import React, { useState } from 'react';
import { Button } from '../components/ui/Button';
import { Input } from '../components/ui/Input';
import { Textarea } from '../components/ui/Textarea';
import { CheckCircle, AlertCircle } from 'lucide-react';
// ─── FORMSPREE CONFIGURATION ───
const WAITLIST_FORM_ID = 'xkovnwlq';
const CONTACT_FORM_ID = 'xdalonrq';
type FormStatus = 'idle' | 'submitting' | 'success' | 'error';
export function EarlyAccessPage() {
  const [waitlistStatus, setWaitlistStatus] = useState<FormStatus>('idle');
  const [contactStatus, setContactStatus] = useState<FormStatus>('idle');
  const [errorMessage, setErrorMessage] = useState('');
  const handleWaitlistSubmit = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    setWaitlistStatus('submitting');
    setErrorMessage('');
    const form = e.currentTarget;
    const data = {
      name: (form.elements.namedItem('name') as HTMLInputElement)?.value,
      email: (form.elements.namedItem('email') as HTMLInputElement)?.value,
      company: (form.elements.namedItem('company') as HTMLInputElement)?.value,
      _subject: 'New MCPGuard Waitlist Signup'
    };
    try {
      const response = await fetch(`https://formspree.io/f/${WAITLIST_FORM_ID}`, {
        method: 'POST',
        body: JSON.stringify(data),
        headers: {
          'Content-Type': 'application/json',
          Accept: 'application/json'
        }
      });
      if (response.ok) {
        setWaitlistStatus('success');
      } else {
        const result = await response.json();
        setWaitlistStatus('error');
        setErrorMessage(result?.errors?.[0]?.message || 'Something went wrong. Please try again.');
      }
    } catch {
      setWaitlistStatus('error');
      setErrorMessage('Network error. Please check your connection and try again.');
    }
  };
  const handleContactSubmit = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    setContactStatus('submitting');
    setErrorMessage('');
    const form = e.currentTarget;
    const data = {
      name: (form.elements.namedItem('name') as HTMLInputElement)?.value,
      email: (form.elements.namedItem('email') as HTMLInputElement)?.value,
      message: (form.elements.namedItem('message') as HTMLTextAreaElement)?.value,
      _subject: 'New MCPGuard Contact Message'
    };
    try {
      const response = await fetch(`https://formspree.io/f/${CONTACT_FORM_ID}`, {
        method: 'POST',
        body: JSON.stringify(data),
        headers: {
          'Content-Type': 'application/json',
          Accept: 'application/json'
        }
      });
      if (response.ok) {
        setContactStatus('success');
      } else {
        const result = await response.json();
        setContactStatus('error');
        setErrorMessage(result?.errors?.[0]?.message || 'Something went wrong. Please try again.');
      }
    } catch {
      setContactStatus('error');
      setErrorMessage('Network error. Please check your connection and try again.');
    }
  };
  return <div className="max-w-4xl mx-auto py-12 px-4 animate-in fade-in duration-500" data-id="element-159">
      <div className="text-center mb-12" data-id="element-160">
        <h1 className="text-4xl font-bold text-[#1e3a5f] mb-4" data-id="element-161">
          Early Access & Contact
        </h1>
        <p className="text-xl text-gray-600 max-w-2xl mx-auto" data-id="element-162">
          Join the waitlist or get in touch with our team to learn more about
          MCPGuard.
        </p>
      </div>

      <div className="grid md:grid-cols-2 gap-12 md:gap-24" data-id="element-163">
        {/* Section 1: Waiting List */}
        <div className="space-y-6" data-id="element-164">
          <div data-id="element-165">
            <h2 className="text-2xl font-bold text-[#1e3a5f] mb-2" data-id="element-166">
              Get Early Access to MCPGuard
            </h2>
            <p className="text-gray-600" data-id="element-167">
              MCPGuard is currently in private preview. Join the waiting list to
              receive early access and product updates.
            </p>
          </div>

          {waitlistStatus === 'success' ? <div className="bg-[#e8f7f4] border border-[#4db8a4]/40 rounded-lg p-6 text-center animate-in zoom-in-95 duration-300" data-id="element-168">
              <CheckCircle className="h-12 w-12 text-[#4db8a4] mx-auto mb-4" data-id="element-169" />
              <h3 className="text-lg font-semibold text-[#1e3a5f] mb-2" data-id="element-170">
                You're on the list!
              </h3>
              <p className="text-[#3a5878]" data-id="element-171">
                We'll be in touch shortly with your access details.
              </p>
            </div> : <form onSubmit={handleWaitlistSubmit} className="space-y-4" data-id="element-172">
              <Input label="Name" name="name" placeholder="Jane Doe" required data-id="element-173" />
              <Input label="Work Email" name="email" type="email" placeholder="jane@company.com" required data-id="element-174" />
              <Input label="Company (Optional)" name="company" placeholder="Acme Corp" data-id="element-175" />

              {waitlistStatus === 'error' && <div className="flex items-center space-x-2 text-sm text-red-600 bg-red-50 border border-red-200 rounded-md px-3 py-2" data-id="element-176">
                  <AlertCircle className="h-4 w-4 flex-shrink-0" data-id="element-177" />
                  <span data-id="element-178">{errorMessage}</span>
                </div>}

              <Button type="submit" className="w-full" isLoading={waitlistStatus === 'submitting'} data-id="element-179">
                Request Access
              </Button>
            </form>}
        </div>

        {/* Divider for mobile */}
        <div className="md:hidden border-t border-gray-200" data-id="element-180" />

        {/* Section 2: Contact */}
        <div className="space-y-6" data-id="element-181">
          <div data-id="element-182">
            <h2 className="text-2xl font-bold text-[#1e3a5f] mb-2" data-id="element-183">
              Talk to Us
            </h2>
            <p className="text-gray-600" data-id="element-184">
              Have questions about AI authorization, auditability, or enterprise
              deployment? We'd love to talk.
            </p>
          </div>

          {contactStatus === 'success' ? <div className="bg-[#e8f7f4] border border-[#4db8a4]/40 rounded-lg p-6 text-center animate-in zoom-in-95 duration-300" data-id="element-185">
              <CheckCircle className="h-12 w-12 text-[#4db8a4] mx-auto mb-4" data-id="element-186" />
              <h3 className="text-lg font-semibold text-[#1e3a5f] mb-2" data-id="element-187">
                Message Sent
              </h3>
              <p className="text-[#3a5878]" data-id="element-188">
                Thank you for reaching out. Our team will respond within 24
                hours.
              </p>
            </div> : <form onSubmit={handleContactSubmit} className="space-y-4" data-id="element-189">
              <Input label="Name" name="name" placeholder="John Smith" required data-id="element-190" />
              <Input label="Email" name="email" type="email" placeholder="john@example.com" required data-id="element-191" />
              <Textarea label="Message" name="message" placeholder="How does MCPGuard handle..." required rows={4} data-id="element-192" />

              {contactStatus === 'error' && <div className="flex items-center space-x-2 text-sm text-red-600 bg-red-50 border border-red-200 rounded-md px-3 py-2" data-id="element-193">
                  <AlertCircle className="h-4 w-4 flex-shrink-0" data-id="element-194" />
                  <span data-id="element-195">{errorMessage}</span>
                </div>}

              <Button type="submit" variant="outline" className="w-full" isLoading={contactStatus === 'submitting'} data-id="element-196">
                Send Message
              </Button>
            </form>}
        </div>
      </div>
    </div>;
}